import { Stack, Typography, useTheme } from "@mui/material";
import { Property } from "src/api/sure-api/ssq/useGetOneSsqVersion";
import { DimensionValue } from "src/api/client-api/olapScores/olapScoresSchema";
import { ValidationMessage } from "./validationTypes.ts";
import { usePropertyContext } from "./context/usePropertyContext.tsx";
import { FieldValue } from "./Field/FieldValue.tsx";
import { GroupMultivalued } from "./Field/Multivalue/Normal/GroupMultivalued.tsx";
import { GroupSimple } from "./Field/Group/GroupSimple.tsx";
import { useId } from "react";
import { useQuestionnaireFormContext } from "src/components/QuestionnaireForm/context/useQuestionnaireForm";

export type FormPropertyProps = {
    property: Property;
    position: number;

    dimensionValue?: DimensionValue;
    gradeDimensionValue?: DimensionValue;

    showSubscore?: boolean;
    accessibleLabel?: string;
    descriptionId?: string;
    invalid?: boolean;
};

export const FormPropertyComponent = (props: FormPropertyProps) => {
    const theme = useTheme();
    const { getValidationError } = usePropertyContext();
    const { appearance = "legacy" } = useQuestionnaireFormContext();
    const generatedId = useId();

    const { property, position } = props;

    if (property.children.length > 0) {
        if (property.multivalue === true) {
            return <GroupMultivalued {...props} />;
        }

        return <GroupSimple {...props} />;
    }

    const { validationMessages, propertyValidationKey, hasValidationMessages } = getValidationError(
        property.id,
        position,
    );
    const hasBlockingError = validationMessages.some(
        ({ severity }) => severity === "error" || severity === "warning_with_block",
    );
    const descriptionId =
        appearance === "product-v2" && hasValidationMessages ?
            `questionnaire-field-description-${generatedId}`
        :   undefined;

    return (
        <Stack width={`100%`}>
            <Stack width="100%" gap="4px">
                <FieldValue
                    {...props}
                    descriptionId={descriptionId}
                    invalid={appearance === "product-v2" && hasBlockingError}
                />
                {hasValidationMessages && !property.multivalue && (
                    <Stack
                        id={descriptionId}
                        role={appearance === "product-v2" && hasBlockingError ? "alert" : undefined}
                        marginTop="8px"
                    >
                        {validationMessages.map((validationMessage: ValidationMessage, index: number) => (
                            <Typography
                                key={`${propertyValidationKey}-validation-${index}`}
                                variant="body-medium"
                                fontSize="14px"
                                fontWeight={500}
                                color={
                                    (
                                        validationMessage.severity === "warning" ||
                                        validationMessage.severity === "warning_with_block"
                                    ) ?
                                        theme.custom.orange[500]
                                    :   theme.custom.red[500]
                                }
                            >
                                {validationMessage.message}
                            </Typography>
                        ))}
                    </Stack>
                )}
            </Stack>
        </Stack>
    );
};
